import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Film, Loader2, FolderOpen, Users, AlertCircle } from 'lucide-react';

export default function InviteAccept() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login, setSession, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/invite-link/${token}`)
      .then(r => setInvite(r.data))
      .catch(e => setError(e.response?.data?.detail || 'This invite link is invalid.'))
      .finally(() => setLoading(false));
  }, [token]);

  const finishAccept = async () => {
    const res = await api.post(`/invite-link/${token}/accept`, { name, password });
    if (res.data.token) {
      setSession(res.data.token, res.data.user);
    } else {
      await refreshUser();
    }
    toast.success('Invite accepted — welcome aboard!');
    navigate(res.data.redirect || '/dashboard');
  };

  const handleNewUser = async (e) => {
    e.preventDefault();
    if (!name.trim() || password.length < 6) { toast.error('Enter your name and a password (6+ chars)'); return; }
    setSubmitting(true);
    try { await finishAccept(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Could not accept invite'); }
    finally { setSubmitting(false); }
  };

  const handleExistingUser = async (e) => {
    e.preventDefault();
    if (!password) { toast.error('Enter your password'); return; }
    setSubmitting(true);
    try {
      await login(invite.invitee_email, password);
      await finishAccept();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(d === 'ACCOUNT_EXISTS' ? 'Please use your account password' : (d || 'Login failed'));
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen bg-[#060608] flex items-center justify-center"><Loader2 className="w-6 h-6 text-[#FF4500] animate-spin" /></div>;
  }

  const bad = error || !invite || invite.expired || invite.status === 'accepted';

  return (
    <div className="min-h-screen bg-[#060608] flex items-center justify-center px-4">
      <div className="w-full max-w-md" data-testid="invite-accept-page">
        <div className="flex items-center gap-2 mb-8 justify-center">
          <div className="w-9 h-9 rounded-lg bg-[#FF4500] flex items-center justify-center"><Film className="w-5 h-5 text-white" /></div>
          <span className="text-xl font-black tracking-tight text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>AvantGarde Media</span>
        </div>

        {bad ? (
          <div className="bg-[#0d0d11] border border-white/10 rounded-2xl p-8 text-center" data-testid="invite-error">
            <AlertCircle className="w-10 h-10 text-[#8A8A93] mx-auto mb-3" />
            <p className="text-white font-semibold mb-1">Invite unavailable</p>
            <p className="text-sm text-[#8A8A93]">
              {error || (invite?.expired ? 'This invite link has expired.' : invite?.status === 'accepted' ? 'This invite has already been accepted.' : 'This invite link is invalid.')}
            </p>
            <button onClick={() => navigate('/login')} className="mt-5 text-sm text-[#FF4500] hover:underline">Go to sign in</button>
          </div>
        ) : (
          <div className="bg-[#0d0d11] border border-white/10 rounded-2xl p-8">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-11 h-11 rounded-xl bg-[#FF4500]/15 text-[#FF4500] flex items-center justify-center">
                {invite.kind === 'project' ? <FolderOpen className="w-5 h-5" /> : <Users className="w-5 h-5" />}
              </div>
              <div>
                <p className="text-sm text-[#8A8A93]"><span className="text-white font-medium">{invite.inviter_name || 'A teammate'}</span> invited you to the {invite.kind === 'project' ? 'project' : 'workspace'}</p>
                <p className="text-white font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>{invite.name}</p>
              </div>
            </div>

            {invite.has_account ? (
              <form onSubmit={handleExistingUser} className="space-y-3" data-testid="invite-login-form">
                <p className="text-sm text-[#8A8A93]">You already have an account. Sign in as <span className="text-white">{invite.invitee_email}</span> to accept.</p>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Your password" data-testid="invite-password"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#121216] border border-white/10 text-white text-sm outline-none focus:border-[#FF4500]/40" />
                <button type="submit" disabled={submitting} data-testid="invite-accept-btn"
                  className="w-full py-2.5 rounded-lg bg-[#FF4500] text-white font-semibold text-sm hover:bg-[#E03C00] disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Sign in & accept
                </button>
              </form>
            ) : (
              <form onSubmit={handleNewUser} className="space-y-3" data-testid="invite-signup-form">
                <p className="text-sm text-[#8A8A93]">Create your account to join. You're signing up as <span className="text-white">{invite.invitee_email}</span>.</p>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Your name" data-testid="invite-name"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#121216] border border-white/10 text-white text-sm outline-none focus:border-[#FF4500]/40" />
                <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Choose a password (6+ chars)" data-testid="invite-password"
                  className="w-full px-4 py-2.5 rounded-lg bg-[#121216] border border-white/10 text-white text-sm outline-none focus:border-[#FF4500]/40" />
                <button type="submit" disabled={submitting} data-testid="invite-accept-btn"
                  className="w-full py-2.5 rounded-lg bg-[#FF4500] text-white font-semibold text-sm hover:bg-[#E03C00] disabled:opacity-50 flex items-center justify-center gap-2">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin" />} Create account & join
                </button>
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
